import React, { useEffect } from 'react';
import { ProductHeader } from './Detail/Header/ProductHeader';
import { AddToCartSection } from './Detail/AddToCart/AddToCartSection';
import ConfiguratorWrapper from './Detail/ConfiguratorWrapper';
import { ProductImageColumn } from './Detail/ProductImageColumn';
import { ProductDescription } from './Detail/Description/ProductDescription';
import { HTML_CLASSES } from '../../../constants/product.constants';
import { ProductProvider, useProductContext } from '../../../contexts/ProductContext';
import { ProductType } from '../../../types/product/detail.types';

// Import types needed internally and re-export all types from the new location for backward compatibility
import type { IDetailViewConfig } from '../../../types/product/detail.types';

export type {
    IProductConfig,
    IConfigPrice,
    IDetailViewConfig,
    ISmartdealB2BConfig,
    IRequiredProductsPrice,
    IProductInterestConfig,
    ISelectedProductInformation,
    ICartButtonConfig,
    IDetailProductBreadcrumb,
} from '../../../types/product/detail.types';

export { ProductType } from '../../../types/product/detail.types';

interface IProps {
    dom: string;
    config: IDetailViewConfig;
}

// Internal component that uses the context
const DetailContent = (): JSX.Element => {
    const {
        setSelectedProductInformation,
        price,
        swatchConfig,
        success,
        error,
        productConfig,
        labels,
    } = useProductContext();

    const messageRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if ((success || error) && messageRef.current) {
            messageRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [success, error]);

    return (
        <>
            <ProductImageColumn />

            <div className={HTML_CLASSES.LAYOUT_PRODUCT_CONTENT}>
                <ProductHeader />

                <div ref={messageRef} className={HTML_CLASSES.LONGTEXT}>
                    {success && <div className={HTML_CLASSES.CART_CALLOUT_SUCCESS}>{success}</div>}
                    {error && <div className={HTML_CLASSES.CART_CALLOUT_DANGER}>{error}</div>}

                    {productConfig.shortDescription && (
                        <p dangerouslySetInnerHTML={{ __html: productConfig.shortDescription }} />
                    )}
                </div>

                {productConfig.type !== ProductType.SIMPLE_PRODUCT_TYPE && (
                    <>
                        <p>
                            <a className={HTML_CLASSES.BUTTON} href="#tech">
                                {labels.lookTechData}
                            </a>
                        </p>
                        <hr />
                    </>
                )}

                {productConfig.eolHtml && (
                    <>
                        <hr />
                        <p dangerouslySetInnerHTML={{ __html: productConfig.eolHtml }} />
                    </>
                )}

                <ConfiguratorWrapper
                    config={swatchConfig}
                    setSelectedProductInformation={setSelectedProductInformation}
                />

                {price && <AddToCartSection />}

                <ProductDescription />
            </div>
        </>
    );
};

// Main Detail component that provides the context
const Detail = (props: IProps): JSX.Element => {
    const { dom, config } = props;

    return (
        <ProductProvider config={config} dom={dom}>
            <DetailContent />
        </ProductProvider>
    );
};

export default Detail;
